import React, { useCallback, useEffect, useRef, useState } from 'react';
import dompurify from 'dompurify';
import { CommentResponse } from 'src/lib/services/api/comment-api';
import { useRequestRevisionVideoPlayerGlobalStore } from 'src/features/revisions/use-request-revision-video-player-global-store';
import { useToast } from 'src/components/ui/use-toast';
import { shallow } from 'zustand/shallow';
import { useRequestRevisionsStore } from 'src/features/revisions/use-revisions-context-provider';
import { useFormatter } from 'src/services/i18n';
import { debounce, isNumber } from 'lodash';
import { formatTime } from 'src/features/video-player/video-player-utils';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from 'src/components/ui/avatar';
import { cn, str } from 'src/lib/utils';
import { Card } from 'src/components/ui/card';
import { If } from 'src/components/If';
import { Button } from 'src/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { Clock2Icon, Copy, Download, Loader2, Pencil, Reply, SmilePlus, Trash2 } from 'lucide-react';
import EmojiPicker from 'src/components/ui/emoji-picker';
import { Badge } from 'src/components/ui/badge';
import { MinimalMediaCard } from 'src/components/MediaCard/minimal-media-card';
import type { MediaObject } from 'src/api/services/MediaClient';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { RevisionChatEditCommentForm } from './revision-chat-edit-comment-form';
import { useRevisionChatCommentReplyStore } from './revision-chat-comment-reply.store';
import { useShallow } from 'zustand/react/shallow';
import { Str } from 'src/helpers';
import { injectMentionPillsInHtml, stripMentionsToPlainText } from 'src/lib/utils/render-mentions';
import { adminMediaService } from 'src/services/media-service';

const { sanitize } = dompurify;

type Props = {
  comment: CommentResponse;
  currentCommentEditing: string;
  setCurrentCommentEditing: React.Dispatch<string>;
  replyDisabled?: boolean;
  /**
   * The viewer may read this conversation but not write into it — a covering
   * editor on a request assigned to someone else (#2653). Unlike
   * `replyDisabled`, existing threads stay openable: the point is to let them
   * read the brand's history, so only the write affordances go.
   */
  readOnly?: boolean;
  reactMutation?: any;
  deleteMutation?: any;
  onSuccessEdit?: () => void;
};

const RevisionChatComment: React.FC<Props> = ({
  comment,
  currentCommentEditing,
  setCurrentCommentEditing,
  replyDisabled,
  readOnly,
  reactMutation,
  deleteMutation,
  onSuccessEdit,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { setReplyComment } = useRevisionChatCommentReplyStore(
    useShallow(({ setReplyComment }) => ({
      setReplyComment,
    })),
  );

  const pageStore = useRequestRevisionVideoPlayerGlobalStore(
    ({ pause, seek, timeFormat, setOnMount }) => ({ pause, seek, timeFormat, setOnMount }),
    shallow,
  );

  const request = useDecoratedRequestContext();

  const {
    revisions,
    selectedRevision,
    selectedRevisionMedia,
    setSelectedRevisionMedia,
    setSelectedRevision,
  } = useRequestRevisionsStore();

  const { formatDateTime } = useFormatter();

  const isCurrentlyMounted = selectedRevisionMedia?.id === comment.media_id;

  // Look the file up across every version of the request rather than only the
  // one the comment is filed under. A reply inherits its parent's revision
  // while the composer stamps the currently-selected revision's media, so a
  // perfectly live file can sit on a different version (#2663) — searching by
  // id keeps that comment clickable instead of dead.
  const ownRevision = revisions.find((r) => r.id === comment.revision);
  const referredRevision =
    (comment.media_id && revisions.find((r) => r.media.some((m) => m?.id === comment.media_id))) ||
    ownRevision;
  const referredMedia = referredRevision?.media.find((m) => m?.id === comment.media_id);
  const referredMediaExists = !!referredMedia;

  // The file is regularly gone from every version by the time anyone reads the
  // thread — an editor re-uploading it soft deletes the old row, which drops it
  // out of `revisions[].media` entirely. The server still resolves it off
  // `media_id` (withTrashed), so name the file rather than telling the reader
  // nothing.
  //
  // `deleted` is the authority on whether the file is actually gone;
  // `referredMediaExists` only says whether the separately-cached revisions
  // payload happens to hold it yet. The two disagree right after an upload —
  // the comment query refetches on its own 10s staleTime, ahead of the request
  // query — and labelling a brand-new file "(removed)" for that window would be
  // its own bug.
  const referencedMedia = comment.referenced_media;
  const referredMediaRemoved = referencedMedia?.deleted === true;
  const referredMediaName = referredMedia?.file_name ?? referencedMedia?.file_name;
  const isOfCurrentRevision = selectedRevision?.id === referredRevision?.id;

  const commentHasTimestamp =
    isNumber(comment.revision_timestamp) && !isNaN(comment.revision_timestamp);

  const formattedTime = commentHasTimestamp
    ? formatTime(comment.revision_timestamp!, {
        format: pageStore.timeFormat,
        // The server ships the removed file's own fps so a timecode/frames
        // format doesn't quietly print a 24fps frame number for 30/60fps
        // footage once the file has left the revision.
        fps: referredMedia?.fps ?? referencedMedia?.fps ?? 24,
      })
    : '';

  const seekToTime = () => {
    if (!commentHasTimestamp || !referredMediaExists) {
      return;
    }

    if (isCurrentlyMounted) {
      pageStore.seek(comment.revision_timestamp!);

      return;
    }

    if (!isOfCurrentRevision) {
      setSelectedRevision(referredRevision?.id);
    }

    setSelectedRevisionMedia(referredMedia.id);
    pageStore.setOnMount(({ seek, pause }) => {
      pause();
      seek(comment.revision_timestamp!);
    });
  };

  const handleDeleteComment = async (id: string) => {
    if (!confirm(t('Are you sure you want to delete this comment?')!)) {
      return;
    }

    deleteMutation.mutate(id);
  };

  // The debounced callback is created once, so it must not close over
  // `reactMutation` directly: the prop is now undefined while the viewer is
  // read-only (#2653), and a mid-session flip back — an admin reassigns the
  // request and React Query refetches — would otherwise leave a stale
  // `undefined` behind and throw on the next click.
  const reactMutationRef = useRef(reactMutation);
  reactMutationRef.current = reactMutation;

  const react = useCallback(
    debounce((commentId: string, reaction: string) => {
      reactMutationRef.current?.(commentId, reaction);
    }, 300),
    [],
  );

  return (
    <div className={'tw-relative tw-flex tw-gap-2'} key={comment.id}>
      {!comment.sender?.me && (
        <Avatar size={'sm'} className={'tw-mt-2'}>
          <AvatarImage
            className={'tw-object-cover'}
            src={
              comment.sender?.avatar?.generated_conversions.square_200 ??
              comment.sender?.avatar?.index_url
            }
            alt={comment.sender?.full_name}
          />
          <AvatarFallback delayMs={200} className={'tw-bg-orange tw-text-white'}>
            {str.acronym(comment.sender?.full_name)}
          </AvatarFallback>
        </Avatar>
      )}
      <Card
        className={cn(
          'tw-group tw-relative tw-max-w-[calc(100%-48px)] tw-whitespace-pre-line tw-p-3 tw-transition-all',
          comment.sender?.me
            ? 'tw-ml-auto tw-bg-brand-200 hover:tw-bg-brand-300 focus:tw-bg-brand-300'
            : comment.sender?.type === 'editor'
              ? 'tw-bg-neutral-100 hover:tw-bg-neutral-200 focus:tw-bg-neutral-200'
              : 'tw-bg-orange-200 hover:tw-bg-orange-300 focus:tw-bg-orange-300',
          currentCommentEditing === comment.id && 'tw-w-full',
        )}
        variant={'noShadow'}
      >
        <If
          when={currentCommentEditing !== comment.id}
          else={
            <RevisionChatEditCommentForm
              comment={comment}
              onCancel={() => setCurrentCommentEditing('')}
              onSuccess={() => {
                setCurrentCommentEditing('');
                onSuccessEdit?.();
              }}
            />
          }
        >
          <If when={!request.isCompleted && selectedRevision?.isLatest}>
            <div
              className={
                'tw-absolute tw-right-1 tw-top-1 tw-flex tw-rounded-md tw-border tw-border-brand-400 tw-bg-white tw-opacity-0 group-hover:tw-opacity-100 group-focus:tw-opacity-100'
              }
            >
              <TooltipProvider>
                {!replyDisabled && !readOnly && (
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => setReplyComment(comment)}
                        variant={'unset'}
                        size={'iconSm'}
                        className={'hover:tw-text-brand-400'}
                      >
                        <Reply size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('Reply in thread')}</TooltipContent>
                  </Tooltip>
                )}
                {reactMutation && (
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <EmojiPicker onSelect={(emoji) => react(comment.id, emoji.native)}>
                        <Button
                          variant={'unset'}
                          size={'iconSm'}
                          className={'hover:tw-text-brand-400'}
                        >
                          <SmilePlus size={16} />
                        </Button>
                      </EmojiPicker>
                    </TooltipTrigger>
                    <TooltipContent>{t('React')}</TooltipContent>
                  </Tooltip>
                )}
                {/* Not gated on readOnly: RequestRevisionCommentPolicy::edit()
                    and delete() key off `$comment->user_id === $user->id` alone,
                    so an editor whose request was reassigned (a routine flow per
                    #2554) can still fix a typo in what they wrote. Read-only
                    describes the request, not your own words on it. */}
                <If when={comment.sender?.me}>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => setCurrentCommentEditing(comment.id)}
                        variant={'unset'}
                        size={'iconSm'}
                        className={'hover:tw-text-brand-400'}
                      >
                        <Pencil size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('Edit')}</TooltipContent>
                  </Tooltip>
                </If>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant={'unset'}
                      size={'iconSm'}
                      className={'hover:tw-text-brand-400'}
                      onClick={() => {
                        toast({ description: 'Text copied to clipboard!' });
                        // Strip @[Name](uuid) markup to "@Name" so a paste into
                        // an unstyled context (Slack, plain editor) reads naturally.
                        navigator.clipboard.writeText(stripMentionsToPlainText(comment.comment));
                      }}
                    >
                      <Copy size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('Copy')}</TooltipContent>
                </Tooltip>
                {deleteMutation && (
                  <If when={comment.sender?.me}>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <Button
                          disabled={deleteMutation.isLoading}
                          variant={'unset'}
                          size={'iconSm'}
                          className={'hover:tw-text-brand-400'}
                          onClick={() => {
                            handleDeleteComment(comment.id);
                          }}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t('Delete')}</TooltipContent>
                    </Tooltip>
                  </If>
                )}
              </TooltipProvider>
            </div>
          </If>

          <div className={'tw-mb-2 tw-flex tw-flex-row tw-flex-wrap'}>
            <div className={'tw-mr-2 tw-text-sm tw-font-semibold tw-text-text-strong'}>
              {comment.sender?.full_name}
            </div>
            <div className={'tw-text-sm tw-text-text-subtle'}>
              {formatDateTime(comment.created_at)}
            </div>
          </div>

          <If when={commentHasTimestamp}>
            <Button
              className={
                'tw-group tw-mb-2 tw-flex tw-items-center tw-justify-start tw-gap-2 tw-pl-0'
              }
              disabled={!referredMediaExists}
              variant={'unset'}
              size={'unset'}
              onClick={seekToTime}
            >
              <Badge
                variant={'brand'}
                className={cn('pointer gap-2 fs-6 group-hover:tw-bg-brand-500')}
              >
                <Clock2Icon size={16} />
                {formattedTime}
              </Badge>
              <If
                when={!!referredMediaName}
                else={
                  // No name to show, but the comment does point at something —
                  // the file row is hard-gone, or this is a customer looking at
                  // an internal collection they don't receive. Say so rather
                  // than leaving a bare clock badge explaining nothing.
                  <If when={!!comment.media_id}>
                    <div className={'tw-pr-2 tw-text-sm tw-italic tw-text-text-subtle'}>
                      {t('File no longer available')}
                    </div>
                  </If>
                }
              >
                <div
                  className={cn(
                    'tw-line-clamp-1 tw-max-w-[70%] tw-overflow-hidden tw-break-all tw-text-sm',
                    referredMediaRemoved
                      ? 'tw-italic tw-text-text-subtle'
                      : 'group-hover:tw-text-brand-500',
                  )}
                >
                  {referredMediaName}
                  {referredMediaRemoved && ` ${t('(removed)')}`}
                </div>
              </If>
            </Button>
          </If>

          <If when={!!comment.comment}>
            <div
              className={'tw-break-words'}
              dangerouslySetInnerHTML={{
                __html: Str.wrapHrefs(
                  injectMentionPillsInHtml(
                    sanitize(comment.comment ?? '', {
                      USE_PROFILES: { html: true },
                    }),
                  ),
                ),
              }}
            />
          </If>

          <If when={!!comment.edited_at}>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className={'tw-mt-0.5 tw-text-sm tw-text-text-subtle'}>({t('Edited')})</div>
                </TooltipTrigger>
                <TooltipContent>{formatDateTime(comment.edited_at!)}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </If>
          <If when={!!comment.media.length}>
            <div className={'tw-mt-1.5 tw-flex tw-flex-col tw-gap-2'}>
              <div className={'tw-flex tw-flex-wrap tw-gap-2'}>
                {comment.media.map((media) => (
                  <MinimalMediaCard key={media.id} media={media} showTitle canView />
                ))}
              </div>
              <If when={comment.media.length > 1}>
                <DownloadAllButton media={comment.media} />
              </If>
            </div>
          </If>
          {!!comment.reactions && (
            <div className={'tw-mt-2 tw-flex tw-flex-wrap tw-gap-0.5'}>
              {Object.keys(comment.reactions).map((emoji: string) => {
                return (
                  <Button
                    onClick={() => react(comment.id, emoji)}
                    key={emoji}
                    disabled={!reactMutation}
                    variant={'unset'}
                    size={'unset'}
                    className={
                      'tw-flex tw-gap-0.5 tw-bg-brand-300/80 tw-px-1 tw-py-0.5 hover:tw-bg-brand-400/80'
                    }
                  >
                    <span>{emoji}</span>
                    <span className={'tw-text-sm'}>{comment.reactions![emoji].length}</span>
                  </Button>
                );
              })}
            </div>
          )}
          {!!comment.replies_count && !!comment.last_reply_at && !replyDisabled && (
            <Button
              onClick={() => setReplyComment(comment)}
              variant={'unset'}
              className={'tw-group/threads-preview tw-mt-3 tw-flex tw-p-0'}
            >
              <div className={'tw-flex tw-gap-2 tw-text-sm'}>
                <span className={'tw-text-brand'}>
                  {comment.replies_count} {comment.replies_count > 1 ? t('replies') : t('reply')}
                </span>
                <span
                  className={
                    'tw-font-normal tw-text-text-subtle group-hover/threads-preview:tw-hidden'
                  }
                >
                  {formatDateTime(comment.last_reply_at)}
                </span>
                <span
                  className={
                    'tw-hidden tw-font-normal tw-text-text-subtle group-hover/threads-preview:tw-flex'
                  }
                >
                  {t('View thread')}
                </span>
              </div>
            </Button>
          )}
        </If>
      </Card>
    </div>
  );
};

const DownloadAllButton: React.FC<{ media: MediaObject[] }> = ({
  media,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleDownloadAll = async () => {
    if (isDownloading) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsDownloading(true);
    toast({ description: t('Downloading {{count}} files...', { count: media.length }) });

    try {
      const result = await adminMediaService.downloadMultipleTracked(media, controller.signal);

      if (controller.signal.aborted) return;

      const { downloaded, failed, skipped, fallbackOpened } = result;

      if (failed > 0 || skipped > 0) {
        toast({
          description: t('{{downloaded}} downloaded, {{failed}} failed, {{skipped}} unavailable', {
            downloaded: downloaded + fallbackOpened,
            failed,
            skipped,
          }),
          variant: 'destructive',
        });
      } else if (fallbackOpened > 0) {
        toast({
          description: t('{{fallbackOpened}} files opened in browser (direct download not supported)', {
            fallbackOpened,
          }),
        });
      } else {
        toast({ description: t('All files downloaded successfully') });
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error('Download all failed:', error);
        toast({
          description: t('Some files failed to download'),
          variant: 'destructive',
        });
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsDownloading(false);
      }
    }
  };

  return (
    <Button
      variant={'outline'}
      size={'sm'}
      className={'tw-w-fit tw-gap-1.5 tw-text-xs'}
      onClick={handleDownloadAll}
      disabled={isDownloading}
    >
      {isDownloading ? (
        <Loader2 size={14} className={'tw-animate-spin'} />
      ) : (
        <Download size={14} />
      )}
      {isDownloading
        ? t('Downloading...')
        : t('Download all ({{count}})', { count: media.length })}
    </Button>
  );
};

export { RevisionChatComment };
